import { useEffect, useRef, useState } from 'react';
import { clsx } from 'clsx';
import { Search, X, SlidersHorizontal, ArrowUpDown } from 'lucide-react';

export interface AssetFilters {
  assetType: string | null;
  sceneTag: string | null;
  channel: string | null;
  playbookId: string | null;
  briefId: string | null;
  brandCompliant: boolean | null;
  quality: number;
  query: string;
  sortBy: 'spend' | 'ctr' | 'score' | 'recent';
  activeOnly: boolean;
}

const ASSET_TYPES: { value: string; label: string }[] = [
  { value: 'hero', label: 'Hero' },
  { value: 'lifestyle', label: 'Lifestyle' },
  { value: 'product_in_context', label: 'Product' },
  { value: 'thumbnail', label: 'Thumbnail' },
  { value: 'video', label: 'Video' },
  { value: 'scene_graph', label: 'Scene Graph' },
  { value: 'transcript', label: 'Transcript' },
];

const CHANNELS: { value: string; label: string; disabled?: boolean }[] = [
  { value: 'meta_facebook', label: 'Facebook' },
  { value: 'meta_instagram', label: 'Instagram' },
  { value: 'tiktok', label: 'TikTok', disabled: true },
  { value: 'google', label: 'Google', disabled: true },
];

interface Props {
  value: AssetFilters;
  onChange: (next: AssetFilters) => void;
  tab?: 'assets' | 'creatives';
  onTabChange?: (tab: 'assets' | 'creatives') => void;
}

export function AssetSearchBar({ value, onChange, tab, onTabChange }: Props) {
  const patch = (p: Partial<AssetFilters>) => onChange({ ...value, ...p });
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [localQuery, setLocalQuery] = useState(value.query);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Sync if external reset
  useEffect(() => {
    setLocalQuery(value.query);
  }, [value.query]);

  const handleQueryChange = (q: string) => {
    setLocalQuery(q);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => patch({ query: q }), 280);
  };

  const activeFilterCount = [
    value.assetType,
    value.channel,
    value.sceneTag,
    value.playbookId,
    value.briefId,
    value.brandCompliant !== null ? value.brandCompliant : null,
    value.quality > 0 ? value.quality : null,
    value.activeOnly ? true : null,
  ].filter(Boolean).length;

  const clearAll = () =>
    onChange({
      assetType: null,
      sceneTag: null,
      channel: null,
      playbookId: null,
      briefId: null,
      brandCompliant: null,
      quality: 0,
      query: '',
      sortBy: 'spend',
      activeOnly: true,
    });

  return (
    <div className="space-y-3">
      {/* ── Primary Search Box ── */}
      <div className="relative group">
        <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-teal-400/20 to-cyan-400/20 blur-sm opacity-0 group-focus-within:opacity-100 transition-opacity" />
        <div className="relative flex items-center bg-white border border-slate-200 group-focus-within:border-teal-400/60 rounded-2xl shadow-sm transition-all overflow-hidden">
          <Search
            size={16}
            className="absolute left-4 text-slate-400 group-focus-within:text-teal-500 transition-colors pointer-events-none"
          />
          <input
            value={localQuery}
            onChange={(e) => handleQueryChange(e.target.value)}
            placeholder="Search assets by type, scene, channel, brief…"
            className="flex-1 pl-11 pr-4 py-3.5 text-sm bg-transparent placeholder-slate-400 focus:outline-none text-slate-700"
          />
          {localQuery && (
            <button
              onClick={() => handleQueryChange('')}
              className="mr-3 text-slate-400 hover:text-slate-600 transition-colors"
            >
              <X size={14} />
            </button>
          )}

          {/* ── Assets / Creatives tab toggle ── */}
          {tab !== undefined && onTabChange && (
            <>
              <div className="h-7 w-px bg-slate-100 mr-3" />
              <div className="flex items-center bg-slate-100 rounded-lg p-0.5 mr-3 gap-0.5">
                {(['assets', 'creatives'] as const).map((opt) => (
                  <button
                    key={opt}
                    onClick={() => onTabChange(opt)}
                    className={clsx(
                      'px-3 py-1 text-[11px] font-semibold capitalize rounded-md transition-all whitespace-nowrap',
                      tab === opt
                        ? 'bg-white text-teal-600 shadow-sm'
                        : 'text-slate-500 hover:text-slate-700',
                    )}
                  >
                    {opt}
                  </button>
                ))}
              </div>
            </>
          )}

          <div className="h-7 w-px bg-slate-100 mr-3" />
          <button
            onClick={() => setShowAdvanced((p) => !p)}
            className={clsx(
              'flex items-center gap-1.5 mr-3 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors',
              showAdvanced
                ? 'bg-teal-50 text-teal-700'
                : 'text-slate-500 hover:bg-slate-50',
            )}
          >
            <SlidersHorizontal size={12} />
            Filters
            {activeFilterCount > 0 && (
              <span className="bg-teal-500 text-white text-[9px] font-bold rounded-full w-4 h-4 flex items-center justify-center">
                {activeFilterCount}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* ── Quick Filter Chips ── */}
      <div className="flex items-center gap-2 flex-wrap">
        {/* Asset type chips */}
        <div className="flex items-center gap-1 flex-wrap">
          {ASSET_TYPES.map((type) => (
            <button
              key={type.value}
              onClick={() =>
                patch({ assetType: value.assetType === type.value ? null : type.value })
              }
              className={clsx(
                'px-2.5 py-1 rounded-full text-[11px] font-medium border transition-all',
                value.assetType === type.value
                  ? 'bg-teal-500 border-teal-500 text-white shadow-sm'
                  : 'bg-white border-slate-200 text-slate-500 hover:border-teal-300 hover:text-teal-600',
              )}
            >
              {type.label}
            </button>
          ))}
        </div>

        <div className="h-5 w-px bg-slate-200" />

        {/* Channel chips */}
        <div className="flex items-center gap-1">
          {CHANNELS.map((c) => (
            <button
              key={c.value}
              disabled={c.disabled}
              onClick={() =>
                !c.disabled && patch({ channel: value.channel === c.value ? null : c.value })
              }
              title={c.disabled ? 'No data available' : undefined}
              className={clsx(
                'px-2.5 py-1 rounded-full text-[11px] font-medium border transition-all',
                c.disabled
                  ? 'opacity-30 cursor-not-allowed bg-white border-slate-200 text-slate-400'
                  : value.channel === c.value
                    ? 'bg-teal-500 border-teal-500 text-white shadow-sm'
                    : 'bg-white border-slate-200 text-slate-500 hover:border-teal-300 hover:text-teal-600',
              )}
            >
              {c.label}
            </button>
          ))}
        </div>

        <div className="h-5 w-px bg-slate-200" />

        {/* Active only toggle — only shown in Creatives mode */}
        {tab === 'creatives' && (
          <button
            onClick={() => patch({ activeOnly: !value.activeOnly })}
            className={clsx(
              'px-2.5 py-1 rounded-full text-[11px] font-medium border transition-all',
              value.activeOnly
                ? 'bg-teal-500 border-teal-500 text-white shadow-sm'
                : 'bg-white border-slate-200 text-slate-500 hover:border-teal-300 hover:text-teal-600',
            )}
          >
            Active only
          </button>
        )}

        {/* Sort — only shown in Creatives mode */}
        {tab === 'creatives' && (
          <>
            <div className="h-5 w-px bg-slate-200" />
            <div className="flex items-center gap-1">
              <ArrowUpDown size={10} className="text-slate-400" />
              {(
                [
                  { value: 'spend', label: 'Spend' },
                  { value: 'ctr', label: 'CTR' },
                  { value: 'score', label: 'Score' },
                ] as const
              ).map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => patch({ sortBy: opt.value })}
                  className={clsx(
                    'px-2.5 py-1 rounded-full text-[11px] font-medium border transition-all',
                    value.sortBy === opt.value
                      ? 'bg-navy-900 border-navy-900 text-white shadow-sm'
                      : 'bg-white border-slate-200 text-slate-500 hover:border-slate-400',
                  )}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </>
        )}

        {activeFilterCount > 0 && (
          <>
            <div className="h-5 w-px bg-slate-200" />
            <button
              onClick={clearAll}
              className="flex items-center gap-1 px-2.5 py-1 text-[11px] text-slate-400 hover:text-red-500 transition-colors"
            >
              <X size={10} />
              Clear all
            </button>
          </>
        )}
      </div>

      {/* ── Advanced Filters Panel ── */}
      {showAdvanced && (
        <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-4 shadow-sm">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="space-y-1.5">
              <label className="text-[10px] font-mono uppercase tracking-wide text-slate-400 block">
                Scene Tag
              </label>
              <input
                value={value.sceneTag ?? ''}
                onChange={(e) => patch({ sceneTag: e.target.value || null })}
                placeholder="e.g. beach"
                className="w-full px-2.5 py-1.5 text-xs border border-slate-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-teal-500/40"
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-[10px] font-mono uppercase tracking-wide text-slate-400 block">
                Strategic Playbook
              </label>
              <input
                value={value.playbookId ?? ''}
                onChange={(e) => patch({ playbookId: e.target.value || null })}
                placeholder="Playbook ID"
                className="w-full px-2.5 py-1.5 text-xs border border-slate-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-teal-500/40"
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-[10px] font-mono uppercase tracking-wide text-slate-400 block">
                Brief
              </label>
              <input
                value={value.briefId ?? ''}
                onChange={(e) => patch({ briefId: e.target.value || null })}
                placeholder="Brief ID"
                className="w-full px-2.5 py-1.5 text-xs border border-slate-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-teal-500/40"
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-[10px] font-mono uppercase tracking-wide text-slate-400 block">
                Brand Compliant
              </label>
              <div className="inline-flex border border-slate-200 rounded-lg overflow-hidden">
                {[
                  { value: null, label: 'Any' },
                  { value: true, label: 'Yes' },
                  { value: false, label: 'No' },
                ].map((opt) => (
                  <button
                    key={String(opt.value)}
                    onClick={() => patch({ brandCompliant: opt.value })}
                    className={clsx(
                      'px-2.5 py-1 text-[10px] font-medium transition-colors',
                      value.brandCompliant === opt.value
                        ? 'bg-teal-500 text-white'
                        : 'bg-white text-slate-500 hover:bg-slate-50',
                    )}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div className="space-y-1.5">
            <label className="text-[10px] font-mono uppercase tracking-wide text-slate-400 block">
              Quality Score ≥{' '}
              <span className="text-teal-600 font-bold">
                {(value.quality * 100).toFixed(0)}%
              </span>
            </label>
            <input
              type="range"
              min={0}
              max={1}
              step={0.05}
              value={value.quality}
              onChange={(e) => patch({ quality: Number(e.target.value) })}
              className="w-full max-w-xs accent-teal-500"
            />
          </div>
        </div>
      )}
    </div>
  );
}

/** @deprecated Use AssetSearchBar instead */
export function AssetFilterRail({ value, onChange }: Props) {
  return <AssetSearchBar value={value} onChange={onChange} />;
}
